import { useEffect, useState } from "react";
import { http, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { KeyRound, CreditCard, Mail, Landmark, MessageCircle, RefreshCw } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function Card({ icon: Icon, title, badge, children }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5 text-sky-300" />
        <h3 className="font-heading font-bold text-lg flex-1">{title}</h3>
        {badge}
      </div>
      {children}
    </div>
  );
}
const Field = ({ label, ...p }) => (
  <label className="block mb-3">
    <span className="text-xs text-slate-400">{label}</span>
    <Input {...p} className="mt-1 bg-white/5 border-white/10 text-slate-100" />
  </label>
);
const Dot = ({ on }) => <span className={`text-xs font-semibold px-2 py-1 rounded-full ${on ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-500/20 text-slate-300"}`}>{on ? "CONFIGURATO" : "NON CONFIGURATO"}</span>;
const OA_STATUS = {
  connected: ["bg-emerald-500/20 text-emerald-300", "CONNECTED"],
  error: ["bg-red-500/20 text-red-300", "ERROR"],
  testing: ["bg-amber-500/20 text-amber-300", "TESTING…"],
  not_configured: ["bg-slate-500/20 text-slate-300", "NON CONFIGURATO"],
};
const OAStatus = ({ status }) => {
  const [cls, label] = OA_STATUS[status] || OA_STATUS.not_configured;
  return <span data-testid="oa-status" className={`text-xs font-semibold px-2 py-1 rounded-full ${cls}`}>{label}</span>;
};

export default function IntegrationsPanel() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [oaKey, setOaKey] = useState("");
  const [oaBusy, setOaBusy] = useState(false);
  const [smode, setSmode] = useState("test");
  const [sf, setSf] = useState({ publishable_key: "", secret_key: "", webhook_secret: "", price_monthly: "", price_annual: "" });
  const [ef, setEf] = useState({ provider: "resend", from_email: "", from_name: "", api_key: "" });
  const [bf, setBf] = useState({});
  const [meta, setMeta] = useState(null);
  const [mf, setMf] = useState({ app_id: "", app_secret: "", config_id: "", graph_version: "", verify_token: "" });
  const [waOverview, setWaOverview] = useState([]);

  const loadMeta = () => {
    http.get("/settings/platform/meta").then((r) => { setMeta(r.data); setMf({ app_id: r.data.app_id || "", app_secret: "", config_id: r.data.config_id || "", graph_version: r.data.graph_version || "v26.0", verify_token: "" }); }).catch(() => {});
    http.get("/settings/platform/whatsapp-overview").then((r) => setWaOverview(r.data.tenants || [])).catch(() => {});
  };
  const saveMeta = async () => {
    try {
      const payload = { app_id: mf.app_id, config_id: mf.config_id, graph_version: mf.graph_version };
      if (mf.app_secret.trim()) payload.app_secret = mf.app_secret.trim();
      if (mf.verify_token.trim()) payload.verify_token = mf.verify_token.trim();
      await http.post("/settings/platform/meta", payload);
      toast.success("Configurazione Meta salvata"); setMf((m) => ({ ...m, app_secret: "", verify_token: "" })); loadMeta();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const regenVerify = async () => { try { await http.post("/settings/platform/meta/regenerate-verify-token"); toast.success("Verify Token rigenerato"); loadMeta(); } catch (e) { toast.error(apiErr(e)); } };


  const load = () => http.get("/settings/integrations").then((r) => {
    setData(r.data); setBf(r.data.bank || {});
    setEf((e) => ({ ...e, provider: r.data.email?.provider || "resend", from_email: r.data.email?.from_email || "", from_name: r.data.email?.from_name || "" }));
    setSmode(r.data.stripe?.active_mode || "test");
  }).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); loadMeta(); }, []);
  if (!data) return <div className="text-slate-400 text-sm">Caricamento…</div>;

  const saveOA = async () => {
    if (!oaKey.trim()) { toast.error("Inserisci una API Key"); return; }
    setOaBusy(true);
    try {
      const { data: r } = await http.post("/settings/ai/openai", { api_key: oaKey.trim() });
      toast.success(r.message || "OpenAI salvata e connessa"); setOaKey(""); await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setOaBusy(false); }
  };
  const testOA = async () => {
    setOaBusy(true);
    try { const { data: r } = await http.post("/settings/ai/openai/test"); toast[r.ok ? "success" : "error"](r.message); await load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setOaBusy(false); }
  };
  const rmOA = async () => { try { await http.delete("/settings/ai/openai"); toast.success("Rimossa"); await load(); } catch (e) { toast.error(apiErr(e)); } };
  const saveStripe = async () => {
    try {
      const body = { mode: smode, enabled: true };
      Object.entries(sf).forEach(([k, v]) => { if (v && v.trim()) body[k] = v.trim(); });
      const { data: r } = await http.post("/settings/integrations/stripe", body);
      const rd = r.readiness || {};
      toast.success(`Stripe ${smode.toUpperCase()} salvato e verificato${r.account ? ` (account ${r.account})` : ""} · checkout ${rd.checkout_ready ? "PRONTO" : "NON pronto"}`);
      setSf({ publishable_key: "", secret_key: "", webhook_secret: "", price_monthly: "", price_annual: "" }); load();
    } catch (e) { toast.error(apiErr(e)); load(); }
  };
  const testStripe = async () => { try { const { data: r } = await http.post("/settings/integrations/stripe/test"); toast[r.ok ? "success" : "error"](r.message); load(); } catch (e) { toast.error(apiErr(e)); } };
  const saveEmail = async () => { try { await http.post("/settings/integrations/email", { ...ef, enabled: true }); toast.success("Email salvata"); setEf((e) => ({ ...e, api_key: "" })); load(); } catch (e) { toast.error(apiErr(e)); } };
  const testEmail = async () => {
    const to = window.prompt("Invia un'email di prova a:", user?.email || "");
    if (!to) return;
    try { const { data: r } = await http.post("/platform/email/test", { to_email: to }); toast[r.status === "SENT" ? "success" : "error"](r.status === "SENT" ? `Email di prova inviata a ${to}` : `Invio non riuscito: ${r.status}${r.detail ? " · " + r.detail : ""}`); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveBank = async () => { try { await http.post("/settings/integrations/bank", bf); toast.success("Dati bancari salvati"); load(); } catch (e) { toast.error(apiErr(e)); } };

  const st = data.stripe[smode];
  return (
    <div className="grid md:grid-cols-2 gap-5" data-testid="integrations-panel">
      <Card icon={KeyRound} title="OpenAI" badge={<OAStatus status={oaBusy ? "testing" : data.openai.status} />}>
        <div className="text-xs text-slate-400 mb-3">Livello piattaforma · {data.openai.mode === "live" ? "LIVE" : "MOCK"} {data.openai.masked ? `· ${data.openai.masked}` : ""}</div>
        <Field label="API Key" type="password" placeholder="sk-…" value={oaKey} onChange={(e) => setOaKey(e.target.value)} data-testid="oa-key" />
        <div className="flex gap-2">
          <Button data-testid="oa-save" onClick={saveOA} disabled={oaBusy} className="bg-sky-600">{oaBusy ? "Attendi…" : "Salva & Testa"}</Button>
          <Button variant="outline" onClick={testOA} disabled={oaBusy} className="border-white/20 bg-transparent text-slate-100">Test</Button>
          {data.openai.configured && <Button variant="outline" onClick={rmOA} disabled={oaBusy} className="border-red-400/30 bg-transparent text-red-300">Rimuovi</Button>}
        </div>
      </Card>

      <Card icon={CreditCard} title="Stripe" badge={<Dot on={!!data.stripe.enabled} />}>
        <div className="flex gap-2 mb-3">
          {["test", "live"].map((m) => (
            <button key={m} data-testid={`stripe-mode-${m}`} onClick={() => setSmode(m)} className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${smode === m ? "bg-sky-500/20 text-sky-300 border-sky-400/40" : "border-white/10 text-slate-400"}`}>{m.toUpperCase()}</button>
          ))}
        </div>
        <Field label="Publishable Key" placeholder={st.publishable || "pk_…"} value={sf.publishable_key} onChange={(e) => setSf({ ...sf, publishable_key: e.target.value })} data-testid="stripe-pk" />
        <Field label="Secret Key" type="password" placeholder={st.secret.masked || "sk_…"} value={sf.secret_key} onChange={(e) => setSf({ ...sf, secret_key: e.target.value })} data-testid="stripe-sk" />
        <Field label="Webhook Secret" type="password" placeholder={st.webhook.masked || "whsec_…"} value={sf.webhook_secret} onChange={(e) => setSf({ ...sf, webhook_secret: e.target.value })} data-testid="stripe-wh" />
        <Field label="Price ID piano mensile (€60/mese)" placeholder={st.price_monthly || "price_…"} value={sf.price_monthly} onChange={(e) => setSf({ ...sf, price_monthly: e.target.value })} data-testid="stripe-price-month" />
        <Field label="Price ID piano annuale (€600/anno)" placeholder={st.price_annual || "price_…"} value={sf.price_annual} onChange={(e) => setSf({ ...sf, price_annual: e.target.value })} data-testid="stripe-price-year" />
        <div className="text-[11px] text-slate-500 mb-3 break-all" data-testid="stripe-webhook-url">Webhook URL (Stripe Dashboard → Developers → Webhooks): {BACKEND}/api/stripe/webhook</div>
        {(() => {
          const rd = data.stripe.readiness || {};
          const miss = { secret_key: "Secret Key", webhook_secret: "Webhook Secret", price_monthly: "Price ID mensile", price_annual: "Price ID annuale" };
          return (
            <div className="text-xs mb-3 space-y-1" data-testid="stripe-status">
              <div>Stato: <span className={data.stripe.enabled ? "text-emerald-300 font-semibold" : "text-amber-300 font-semibold"}>{data.stripe.enabled ? `ATTIVO (${data.stripe.active_mode.toUpperCase()})` : "NON ATTIVO"}</span>{rd.key_source === "env" && <span className="text-slate-500"> · chiave da ambiente</span>}</div>
              <div data-testid="stripe-checkout-readiness">Checkout clienti: <span className={rd.checkout_ready ? "text-emerald-300 font-semibold" : "text-red-300 font-semibold"}>{rd.checkout_ready ? "PRONTO" : "NON PRONTO"}</span>
                {!rd.checkout_ready && (rd.missing || []).length > 0 && <span className="text-slate-400"> · manca: {(rd.missing || []).map((k) => miss[k] || k).join(", ")}</span>}
                {st.prices_verified_at && <span className="text-slate-500"> · prezzi verificati {new Date(st.prices_verified_at).toLocaleDateString("it-IT")}</span>}
              </div>
            </div>
          );
        })()}
        <div className="flex gap-2">
          <Button data-testid="stripe-save" onClick={saveStripe} className="bg-sky-600">Salva & Verifica {smode.toUpperCase()}</Button>
          <Button data-testid="stripe-test" variant="outline" onClick={testStripe} className="border-white/20 bg-transparent text-slate-100">Test</Button>
        </div>
      </Card>

      <Card icon={Mail} title="Email" badge={<Dot on={data.email.api_key.configured} />}>
        <label className="block mb-3"><span className="text-xs text-slate-400">Provider</span>
          <select data-testid="email-provider" value={ef.provider} onChange={(e) => setEf({ ...ef, provider: e.target.value })} className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm">
            <option value="resend" className="bg-[#0B1120]">Resend</option>
            <option value="sendgrid" className="bg-[#0B1120]">SendGrid</option>
            <option value="smtp" className="bg-[#0B1120]">SMTP</option>
          </select></label>
        <Field label="From email" value={ef.from_email} onChange={(e) => setEf({ ...ef, from_email: e.target.value })} data-testid="email-from" />
        <Field label="From name" value={ef.from_name} onChange={(e) => setEf({ ...ef, from_name: e.target.value })} data-testid="email-fromname" />
        <Field label="API Key" type="password" placeholder={data.email.api_key.masked || ""} value={ef.api_key} onChange={(e) => setEf({ ...ef, api_key: e.target.value })} data-testid="email-key" />
        <div className="flex gap-2">
          <Button data-testid="email-save" onClick={saveEmail} className="bg-sky-600">Salva</Button>
          <Button data-testid="email-test" variant="outline" onClick={testEmail} className="border-white/20 bg-transparent text-slate-100">Invia email di prova</Button>
        </div>
      </Card>

      <Card icon={Landmark} title="Dati bancari NEXTMIND">
        {[["beneficiary", "Beneficiario"], ["iban", "IBAN"], ["bic", "BIC/SWIFT"], ["bank", "Banca"], ["causale", "Causale base"], ["admin_email", "Email amministrativa"]].map(([k, l]) => (
          <Field key={k} label={l} value={bf[k] || ""} onChange={(e) => setBf({ ...bf, [k]: e.target.value })} data-testid={`bank-${k}`} />
        ))}
        <label className="block mb-3"><span className="text-xs text-slate-400">Istruzioni bonifico</span>
          <textarea rows={2} value={bf.instructions || ""} onChange={(e) => setBf({ ...bf, instructions: e.target.value })} className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="bank-instructions" /></label>
        <Button data-testid="bank-save" onClick={saveBank} className="bg-sky-600">Salva dati bancari</Button>
      </Card>

      <Card icon={MessageCircle} title="Meta / WhatsApp Platform" badge={<Dot on={!!meta?.ready} />}>
        <div className="text-xs text-slate-400 mb-3">App Meta centrale per l'Embedded Signup dei tenant. I clienti non inseriscono token: collegano il proprio WhatsApp da Impostazioni.</div>
        <Field label="Meta App ID" placeholder="1234567890" value={mf.app_id} onChange={(e) => setMf({ ...mf, app_id: e.target.value })} data-testid="meta-app-id" />
        <Field label="Meta App Secret" type="password" placeholder={meta?.app_secret?.configured ? (meta.app_secret.masked || "••••") : "app secret"} value={mf.app_secret} onChange={(e) => setMf({ ...mf, app_secret: e.target.value })} data-testid="meta-app-secret" />
        <Field label="Config ID (Embedded Signup)" placeholder="config id" value={mf.config_id} onChange={(e) => setMf({ ...mf, config_id: e.target.value })} data-testid="meta-config-id" />
        <Field label="Graph API version" placeholder="v26.0" value={mf.graph_version} onChange={(e) => setMf({ ...mf, graph_version: e.target.value })} data-testid="meta-graph-version" />
        <div className="text-[11px] text-slate-500 mb-2 break-all">Webhook URL: {meta?.webhook_url || `${BACKEND}/api/whatsapp/webhook`}</div>
        <Field label="Webhook Verify Token" type="password" placeholder={meta?.verify_token?.configured ? (meta.verify_token.masked || "••••") : "imposta il tuo verify token"} value={mf.verify_token} onChange={(e) => setMf({ ...mf, verify_token: e.target.value })} data-testid="meta-verify-token" />
        <div className="text-[11px] mb-3" data-testid="meta-verify-token-status">Stato Verify Token: {meta?.verify_token?.configured ? <span className="text-emerald-400">Configurato 🔒</span> : <span className="text-amber-300/80">Non configurato</span>} — inseriscilo identico nel Meta Dashboard (WhatsApp → Configuration → Webhook).</div>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="meta-save" onClick={saveMeta} className="bg-sky-600">Salva</Button>
          {meta?.verify_token?.configured && <Button variant="outline" onClick={regenVerify} className="border-white/20 bg-transparent text-slate-100 gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> Rigenera Verify Token</Button>}
        </div>
        {!meta?.ready && <div className="text-[11px] text-amber-300/80 mt-3">Finché App ID, App Secret e Config ID non sono configurati, i tenant vedono "Configurazione Meta Platform necessaria" e non possono collegare WhatsApp.</div>}
      </Card>

      <Card icon={MessageCircle} title="WhatsApp per tenant">
        <div className="text-xs text-slate-400 mb-3">Stato collegamento WhatsApp di ogni azienda (sola lettura).</div>
        <div className="space-y-2" data-testid="wa-tenant-overview">
          {waOverview.length === 0 ? <div className="text-sm text-slate-500">Nessun tenant.</div> : waOverview.map((tn) => {
            const c = tn.status_code === "connected" ? "bg-emerald-500/20 text-emerald-300" : tn.status_code === "error" ? "bg-red-500/20 text-red-300" : "bg-slate-500/20 text-slate-300";
            return (
              <div key={tn.tenant_id} className="flex items-center justify-between border border-white/10 rounded-lg px-3 py-2">
                <div><div className="text-sm font-semibold text-slate-100">{tn.name}</div>
                  <div className="text-[11px] text-slate-500">{tn.number_masked || "—"}{tn.connected_at ? ` · ${new Date(tn.connected_at).toLocaleDateString()}` : ""}</div></div>
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${c}`}>{tn.status}</span>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
